import { format, precisionRound } from 'd3';
import type React from 'react';
import type { ComponentType } from 'react';
import type { z } from 'zod';

import type { SkeletonProps } from './skeletonProps.ts';

/**
 * Formats a numeric value with adaptive precision based on d3 formatting.
 *
 * Uses d3's `precisionRound` to determine optimal decimal places, then formats
 * with the specified format type (default: general rounding).
 *
 * @param input - The number to format
 * @param precision - Target precision for rounding (default: 0.1)
 * @param type - d3 format specifier type (default: 'r' for rounded)
 * @returns Formatted string representation of the number
 *
 * @example
 * formatValue(1234.5678) // "1234.6"
 * formatValue(0.00123, 0.001) // "0.00123"
 *
 * @see https://d3js.org/d3-format for format specifier details
 */
export const formatValue = (input: number, precision = 0.1, type = 'r') =>
  format(`.${precisionRound(precision, input)}~${type}`)(input);

/**
 * Combined props type for grid components.
 *
 * Component-specific schema properties with additional React props.
 * Uses z.input for schema props to accept optional fields (before parsing).
 * Components parse input through schema internally to apply defaults.
 *
 * @typeParam T - Zod schema shape defining component-specific properties
 * @typeParam ComponentPropsType - Additional React props (default: empty object)
 */
type GridComponentProps<T extends z.ZodRawShape, ComponentPropsType> = z.input<z.ZodObject<T>> &
  ComponentPropsType & {
    ref?: React.Ref<HTMLDivElement>;
  };

/**
 * Grid component with attached schema and optional skeleton.
 *
 * Property naming follows React conventions for static component properties
 * (e.g., React.Fragment, styled-components pattern).
 */
export interface GridComponentWithSchema<PropsType> extends React.FC<Omit<PropsType, 'type'>> {
  $$schema: z.ZodObject<{ type: z.ZodLiteral<string> } & z.ZodRawShape>;
  // biome-ignore lint/style/useNamingConvention: Skeleton follows React conventions for static component properties
  Skeleton?: ComponentType<SkeletonProps>;
}

/**
 * Factory function that creates a typed dashboard grid component with attached schema metadata.
 *
 * This is the primary way to define dashboard components that can be rendered within
 * a `ComponentGrid`. Each component created this way:
 *
 * 1. Has its props typed according to the provided Zod schema
 * 2. Carries an attached `$$schema` property for runtime schema validation
 * 3. Gets a proper `name` property derived from the schema's `type` literal (required for HMR)
 * 4. Optionally has a `.Skeleton` subcomponent for loading states
 *
 * @typeParam SchemaType - Zod object shape with a required `type` literal discriminator
 * @typeParam ComponentPropsType - Additional React props not in the schema (default: empty object)
 *
 * @param component - React component function that receives typed props (excluding 'type')
 * @param schema - Zod schema defining component-specific properties (must include `type: z.literal('...')`)
 * @param skeleton - Optional skeleton component for loading/streaming states
 * @returns Enhanced component with attached `$$schema` property for runtime validation
 *
 * @example
 * // Simple grid component with typed schema and skeleton
 * export const Kpi = createGridComponent(
 *   ({ value, label, unit }) => (
 *     <Paper>
 *       <Text>{value} {unit}</Text>
 *       <Text>{label}</Text>
 *     </Paper>
 *   ),
 *   z.object({
 *     type: z.literal('kpi'),
 *     value: z.number(),
 *     label: z.string(),
 *     unit: z.string().optional(),
 *   }),
 *   KpiSkeleton,
 * );
 *
 * // Access the schema for validation
 * Kpi.$$schema.parse({ type: 'kpi', value: 42, label: 'Score' });
 *
 * // Access the skeleton component
 * <Kpi.Skeleton />
 */

/** Convert kebab-case to PascalCase: 'metric-alert' → 'MetricAlert' */
const kebabToPascal = (str: string) =>
  str
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

export function createGridComponent<
  SchemaType extends { type: z.ZodLiteral<string> } & z.ZodRawShape,
  ComponentPropsType = object,
>(
  component: (props: Omit<GridComponentProps<SchemaType, ComponentPropsType>, 'type'>) => React.JSX.Element,
  schema: z.ZodObject<SchemaType>,
  skeleton?: ComponentType<SkeletonProps>,
): GridComponentWithSchema<GridComponentProps<SchemaType, ComponentPropsType>> {
  const GridComponent = Object.assign(component, {
    // biome-ignore lint/style/useNamingConvention: $$schema is an intentional convention for attaching schema metadata
    $$schema: schema,
    ...(skeleton && { Skeleton: skeleton }),
  });

  // Derive PascalCase component name from kebab-case schema type (required for HMR)
  Object.defineProperty(GridComponent, 'name', {
    get: () => kebabToPascal(schema.shape.type.value),
  });

  return GridComponent as GridComponentWithSchema<GridComponentProps<SchemaType, ComponentPropsType>>;
}
